import { Icon } from "./Icon";
import React, { useState, useRef } from 'react';
import { X, Upload } from 'lucide-react';


interface ImageFormData {
    imagen: File | null;
}

interface UserFormCardProps {
    creating: boolean;
    isVisible: boolean;
    personId: string;
    onClose: () => void;
    onSubmit: (formData: ImageFormData, person_id: string) => Promise<{ success: boolean, personId?: string, message?: string }>;
    initialData?: Partial<ImageFormData>;
}

export const PhotoFormCard: React.FC<UserFormCardProps> = ({
    creating,
    isVisible,
    personId,
    onClose,
    onSubmit
}) => {
    const [formData, setFormData] = useState<ImageFormData>({
        imagen: null
    });
    const [dragActive, setDragActive] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [stateSendForm, setStateSendForm] = useState(false);
    const [notification, setNotification] = useState(false);

    const handleImageChange = (file: File) => {
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({
                ...prev,
                imagen: file
            }));

            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleImageChange(e.dataTransfer.files[0]);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            handleImageChange(e.target.files[0]);
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async () => {
        if (!formData.imagen) {
            alert('Por favor, agrega una imagen');
            return;
        }

        const response = await onSubmit(formData, personId);
        
        if (response.success) {
            setStateSendForm(true);
            removeDataForm();
        } else {
            setStateSendForm(false);
        }
        setNotification(true);
    };

    const removeDataForm = () => {
        setFormData({
            imagen: null
        });
        setImagePreview(null);
        const timeout = setTimeout(() => {
            setNotification(false);
            onClose();
        }, 1500);
        return () => {
            clearTimeout(timeout);
        };
    };

    const removeImage = () => {
        setFormData(prev => ({
            ...prev,
            imagen: null
        }));
        setImagePreview(null);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <>
            {/* Modal Overlay */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Subir Imagen</h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                        >
                            <X size={20} className="text-gray-500 dark:text-gray-400" />
                        </button>
                    </div>

                    {/* Contenido */}
                    <div className="space-y-6">
                        {/* Zona de imagen */}
                        <div
                            className={`relative h-48 w-full border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 ${dragActive
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                                }`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="hidden"
                            />

                            {imagePreview ? (
                                <div className="relative h-full">
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        className="h-full w-full object-cover rounded-lg"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="h-full flex flex-col items-center justify-center space-y-4">
                                    <Upload className="w-12 h-12 text-gray-400" />
                                    <div className="space-y-2">
                                        <p className="text-gray-600 dark:text-gray-300 text-sm">
                                            Arrastra una imagen aquí
                                        </p>
                                        <div className="text-gray-400 text-xs">o</div>
                                    </div>

                                    <div className="space-y-2 w-full">
                                        <button
                                            type="button"
                                            onClick={openFileDialog}
                                            className="w-full bg-gray-100 dark:bg-gray-600 dark:text-white hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-200 text-sm"
                                        >
                                            Seleccionar archivo
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Notificación */}
                        {notification && (
                            <div className={`flex flex-row border ${stateSendForm
                                ? "text-green-700 bg-green-50 border-green-200"
                                : "text-red-700 bg-red-50 border-red-200"
                                } text-bold p-3 text-sm rounded-lg`}>
                                <Icon name={`${stateSendForm ? "check" : "close"}`} className="w-4 h-4 mr-2" />
                                <span>{stateSendForm ? "Imagen subida exitosamente!" : "Error al subir imagen"}</span>
                            </div>
                        )}

                        {/* Botones de acción */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                            >
                                Cancelar
                            </button>

                            <button
                                type="button"
                                disabled={creating || !formData.imagen}
                                onClick={handleSubmit}
                                className={`flex items-center gap-2 px-6 py-2 text-sm font-semibold rounded-lg transition-colors duration-200 ${creating || !formData.imagen
                                    ? "bg-blue-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700"
                                    } text-white`}
                            >
                                {creating && (
                                    <Icon name="loading" className="w-4 h-4 text-primary animate-spin" />
                                )}
                                {creating ? "Subiendo..." : "Subir Imagen"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};